import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { DisclaimerModal } from '@/components/modals';
import { DISCLAIMER_MODAL_NAME } from '@/components/modals/disclaimer-modal/types';
import { useModalContext } from '@/components/modals/modal-context';
import { useBasePath } from '@/hooks/use-base-path';
import { useCustomRouting } from '@/hooks/use-custom-routing';
import { useSafeHarborDisclosure } from '@/hooks/use-safe-harbor-disclaimer';
import { PREFIX_URL } from '@/lib/constants';
import { cn, isHospitalSiteType } from '@/lib/utils';
import { useOasApp } from '@/providers/oas-app-provider';
import { NeuFeedback } from '@hcahealthcaredpx/neutron-react';

import { ChangeLocationListModal } from '../../components/change-location/list-modal';
import { Filters, List } from './components';
import { useListingContext } from './hooks';
import styles from './listing.module.css';
import { ListingProps } from './listing.types';

export const OASListing = ({ rendering }: ListingProps): JSX.Element => {
  const fields = useOasApp();
  const { coids, program, siteType } = fields;

  const { routerPush } = useCustomRouting();
  const { withDisclaimer, checkDisclaimerStatus } = useSafeHarborDisclosure();
  const { closeModal, getCallbackData } = useModalContext();
  const { setSearchValue } = useListingContext();
  const { isReady } = useRouter();
  const { withBasePath } = useBasePath();
  const [showSafeHarborDisclaimer, setShowSafeHarborDS] = useState(false);
  const isHospital = isHospitalSiteType(siteType);
  const clearButtonRef = useRef(null);

  useEffect(() => {
    if (isHospital) {
      setShowSafeHarborDS(checkDisclaimerStatus());
      withDisclaimer(() => {}, siteType);
    }
    // eslint-disable-next-line
  }, [isHospital, siteType]);

  useEffect(() => {
    const onBackButton = () => {
      setSearchValue(undefined);
    };

    window.addEventListener('popstate', onBackButton);
    return () => {
      window.removeEventListener('popstate', onBackButton);
    };
  }, [setSearchValue]);

  const handleDecline = () => {
    const homePage = PREFIX_URL ? PREFIX_URL : '/';
    const url = withBasePath(homePage);
    routerPush(url);
  };

  if (showSafeHarborDisclaimer) {
    return (
      <DisclaimerModal
        modalName={DISCLAIMER_MODAL_NAME.SAFE_HARBOR}
        onDecline={handleDecline}
        onSuccess={() => setShowSafeHarborDS(false)}
      />
    );
  }

  const onHealowModalSuccess = () => {
    const onlineSchedulingUrl = getCallbackData(DISCLAIMER_MODAL_NAME.HEALOW_BOOKING);
    if (onlineSchedulingUrl && typeof onlineSchedulingUrl === 'string') {
      window.open(onlineSchedulingUrl, '_blank');
    }
  };

  const closeHealowModal = () => {
    closeModal(DISCLAIMER_MODAL_NAME.HEALOW_BOOKING);
  };

  if (!isReady) {
    return <NeuFeedback kind="spinner" color="blue" />;
  }

  return (
    <div
      data-test-id={rendering?.componentName}
      className={cn('d-flex justify-content-center neu-bg-plain-0', styles.bodyWrapper)}
    >
      <div className="container d-flex w-100 flex-lg-nowrap p-0 gap-2 gap-lg-0 flex-wrap">
        <Filters
          coids={coids}
          program={program}
          siteType={siteType}
          clearButtonRef={clearButtonRef}
        />
        <List clearButtonRef={clearButtonRef} />
        <ChangeLocationListModal />
        <DisclaimerModal
          onSuccess={onHealowModalSuccess}
          onDecline={closeHealowModal}
          titleTranslationKey="OAS.Profile.Details.BookAppointmentModalTitle"
          acceptButtonTranslationKey="OAS.Profile.Details.BookAppointmentModalConfirm"
          declineButtonTranslationKey="OAS.Profile.Details.BookAppointmentModalCancel"
          languageDropdownTranslationKey="OAS.Profile.Details.BookAppointmentModalLanguage"
          modalName={DISCLAIMER_MODAL_NAME.HEALOW_BOOKING}
        />
      </div>
    </div>
  );
};
